import type { ReactNode } from 'react'

import { cn } from '@/lib/utils'

type AlertVariant = 'destructive' | 'warning' | 'info'

const styles: Record<AlertVariant, string> = {
  destructive:
    'border-destructive/30 bg-destructive/10 text-destructive-foreground shadow-sm [&_svg]:text-destructive',
  warning:
    'border-[color-mix(in_oklch,var(--warning)_40%,transparent)] bg-[color-mix(in_oklch,var(--warning)_12%,transparent)] text-foreground shadow-sm [&_svg]:text-[var(--warning)]',
  info: 'border-border bg-card text-card-foreground shadow-[var(--shadow-soft)] [&_svg]:text-primary',
}

export function Alert({
  variant = 'info',
  title,
  children,
  className = '',
}: {
  variant?: AlertVariant
  title?: string
  children: ReactNode
  className?: string
}) {
  const Icon =
    variant === 'destructive' ? AlertCircleIcon : variant === 'warning' ? AlertTriangleIcon : InfoIcon

  return (
    <div role="alert" className={cn('flex gap-3.5 rounded-2xl border px-4 py-3.5', styles[variant], className)}>
      <Icon className="mt-0.5 h-5 w-5 shrink-0" aria-hidden />
      <div className="min-w-0 flex-1">
        {title ? <p className="text-sm font-semibold tracking-tight">{title}</p> : null}
        <div
          className={cn(
            'text-sm leading-relaxed',
            title && 'mt-1 text-muted-foreground',
            variant === 'destructive' && 'text-destructive',
            variant === 'warning' && 'text-foreground/90',
            variant === 'info' && 'text-muted-foreground',
          )}
        >
          {children}
        </div>
      </div>
    </div>
  )
}

function InfoIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z"
      />
    </svg>
  )
}

function AlertCircleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z"
      />
    </svg>
  )
}

function AlertTriangleIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"
      />
    </svg>
  )
}
